import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";

function dateParts(value) {
  if (!value) {
    return null;
  }
  const [year, month, day] = String(value).split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

export function showDate(value) {
  const date = dateParts(value);
  if (!date) {
    return "";
  }
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function StubDate({ value }) {
  const date = dateParts(value);
  if (!date) {
    return null;
  }
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const rest = date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  return (
    <span className="admit-date">
      <span className="admit-weekday">{weekday}</span>
      <span>{rest}</span>
    </span>
  );
}

export function todayKey() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export function money(value) {
  const amount = Number(value);
  if (!amount) {
    return "";
  }
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function clockLabel(value) {
  if (!value) {
    return "";
  }
  const [hourText, minute = "00"] = String(value).split(":");
  let hour = Number(hourText);
  if (!Number.isFinite(hour)) {
    return "";
  }
  const suffix = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${suffix}`;
}

function lunchHours(lunch) {
  const start = clockLabel(lunch.start_time);
  const end = clockLabel(lunch.end_time);
  if (start && end) {
    return `${start} – ${end}`;
  }
  return start || end;
}

export function scheduleWhen(line) {
  const start = clockLabel(line?.starts);
  const end = clockLabel(line?.ends);
  if (start && end) {
    return `${start} – ${end}`;
  }
  return start;
}

export const TICKETS_PER_CAR = 2;

export function includedSpectatorTickets(carCount) {
  const cars = Number(carCount) || 0;
  return cars > 0 ? cars * TICKETS_PER_CAR : 0;
}

export function paidSpectatorTickets(carCount, quantity) {
  return Math.max(0, (Number(quantity) || 0) - includedSpectatorTickets(carCount));
}

export function spectatorHolding(previousCars, nextCars, quantity) {
  const previousFree = includedSpectatorTickets(previousCars);
  const nextFree = includedSpectatorTickets(nextCars);
  const paid = Math.max(0, (Number(quantity) || 0) - previousFree);
  return nextFree + paid;
}

export function freeTicketNote(carCount) {
  const cars = Number(carCount) || 0;
  const tickets = includedSpectatorTickets(cars);
  if (tickets < 1) {
    return "";
  }
  const ticketWord = tickets === 1 ? "ticket" : "tickets";
  const carWord = cars === 1 ? "car" : "cars";
  return `* ${tickets} free ${ticketWord} for ${cars} registered ${carWord}`;
}

export function yearSpan(item) {
  if (item.year_start && item.year_end) {
    return `${item.year_start}–${item.year_end}`;
  }
  if (item.year_start) {
    return `${item.year_start} and later`;
  }
  if (item.year_end) {
    return `Through ${item.year_end}`;
  }
  return "Any year";
}

const typeLabel = {
  all: "Auto and motorcycle",
  automobile: "Automobile",
  motorcycle: "Motorcycle",
};

export function typeName(value) {
  return typeLabel[value] || value;
}

const carTypeLabel = {
  automobile: "Automobile",
  motorcycle: "Motorcycle",
};

function carTitle(car) {
  return [car.year, car.manufacture_name, car.model].filter(Boolean).join(" ");
}

function shown(value) {
  const text = String(value || "").trim();
  return text || "N/A";
}

function carPhotos(car) {
  const photos = (car.images || []).filter((photo) => photo.display || photo.thumb);
  if (photos.length) {
    return photos;
  }
  return car.image ? [{ id: "cover", display: car.image, thumb: car.image }] : [];
}

function marqueeSlides(items) {
  if (items.length < 3) {
    return { slides: items, still: true };
  }
  return { slides: [...items, ...items], still: false };
}

function entryDate(value) {
  if (!value) {
    return "";
  }
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function CarSection({ id, title, count, open, onToggle, children }) {
  return (
    <article className={open ? "car-detail-card is-open" : "car-detail-card"}>
      <h3>
        <button type="button" aria-expanded={open} aria-controls={id} onClick={onToggle}>
          <span>
            {title}
            {count == null ? null : ` (${count})`}
          </span>
          <span className="car-detail-chevron" aria-hidden="true" />
        </button>
      </h3>
      <div className="accordion-fold">
        <div className="accordion-panel" inert={!open}>
          <div className="car-detail-panel" id={id}>
            {children}
          </div>
        </div>
      </div>
    </article>
  );
}

function PhotoStrip({ photos, onOpen }) {
  const { slides, still } = marqueeSlides(photos);
  return (
    <div className={still ? "logo-marquee is-still" : "logo-marquee"} aria-label="Car photos">
      <div className="logo-track">
        {slides.map((photo, index) => (
          <button
            key={`${photo.id}-${index}`}
            type="button"
            className="logo-hit"
            onClick={() => onOpen(photo)}
          >
            <img src={photo.thumb || photo.display} alt="" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function CarCarousel({ cars }) {
  const [car, setCar] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [section, setSection] = useState("details");
  if (!cars.length) {
    return null;
  }
  const { slides, still } = marqueeSlides(cars);
  const photos = car ? carPhotos(car) : [];
  const closeCar = () => {
    setPhoto(null);
    setCar(null);
  };

  return (
    <>
      <div className={still ? "car-marquee is-still" : "car-marquee"} aria-label="Member cars">
        <div className="car-track">
          {slides.map((item, index) => (
            <button
              key={`${item.id}-${index}`}
              type="button"
              className="car-card"
              onClick={() => {
                setSection("details");
                setCar(item);
              }}
            >
              <img src={item.image} alt="" />
              <strong className="car-card-copy">{carTitle(item)}</strong>
            </button>
          ))}
        </div>
      </div>
      <Modal show={Boolean(car)} onHide={closeCar} centered className="class-detail-modal car-detail-modal">
        <Modal.Header closeButton>
          <Modal.Title>{car ? carTitle(car) : ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {car ? (
            <>
              <div className="car-detail-scroll">
                <CarSection
                  id="car-details"
                  title="Details"
                  open={section === "details"}
                  onToggle={() => setSection((current) => (current === "details" ? "" : "details"))}
                >
                  <dl className="class-detail-facts">
                    <div>
                      <dt>Year</dt>
                      <dd>{shown(car.year)}</dd>
                    </div>
                    <div>
                      <dt>Manufacture</dt>
                      <dd className="car-detail-make">
                        {car.manufacture_logo ? <img src={car.manufacture_logo} alt="" /> : null}
                        <span>{shown(car.manufacture_name)}</span>
                      </dd>
                    </div>
                    <div>
                      <dt>Model</dt>
                      <dd>{shown(car.model)}</dd>
                    </div>
                    <div>
                      <dt>Type</dt>
                      <dd>{carTypeLabel[car.vehicle_type] || shown(car.vehicle_type)}</dd>
                    </div>
                    <div>
                      <dt>Exterior color</dt>
                      <dd>{shown(car.exterior_color)}</dd>
                    </div>
                    <div>
                      <dt>Condition</dt>
                      <dd>{shown(car.condition)}</dd>
                    </div>
                    <div>
                      <dt>Concours</dt>
                      <dd>{car.concours ? "Yes" : "No"}</dd>
                    </div>
                  </dl>
                </CarSection>
                <CarSection
                  id="car-awards"
                  title="Awards"
                  count={(car.awards || []).length}
                  open={section === "awards"}
                  onToggle={() => setSection((current) => (current === "awards" ? "" : "awards"))}
                >
                  {(car.awards || []).length ? (
                    <ul className="car-detail-list">
                      {car.awards.map((award) => (
                        <li key={award.id}>
                          <strong>{award.car_class}</strong>
                          <span>{award.trophy || "Class winner"}</span>
                          <span>
                            {award.event_title} · {entryDate(award.event_date)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="car-detail-empty">No awards yet.</p>
                  )}
                </CarSection>
                <CarSection
                  id="car-entries"
                  title="Past entries"
                  count={(car.entries || []).length}
                  open={section === "entries"}
                  onToggle={() => setSection((current) => (current === "entries" ? "" : "entries"))}
                >
                  {(car.entries || []).length ? (
                    <ul className="car-detail-list">
                      {car.entries.map((entry) => (
                        <li key={entry.id}>
                          <strong>{entry.car_class}</strong>
                          <span>{entry.judging ? "Judged" : "Not judged"}</span>
                          <span>
                            {entry.event_title} · {entryDate(entry.event_date)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="car-detail-empty">No past entries yet.</p>
                  )}
                </CarSection>
              </div>
              {photos.length ? <PhotoStrip photos={photos} onOpen={setPhoto} /> : null}
            </>
          ) : null}
        </Modal.Body>
      </Modal>
      <Modal
        show={Boolean(photo)}
        onHide={() => setPhoto(null)}
        centered
        className="class-detail-modal car-photo-modal"
        backdropClassName="car-photo-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>{car ? carTitle(car) : "Photo"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {photo ? <img src={photo.display || photo.thumb} alt="" /> : null}
        </Modal.Body>
      </Modal>
    </>
  );
}

export function LogoCarousel({ makes, quiet = false }) {
  const [make, setMake] = useState(null);
  const logos = makes.filter((item) => item.logo_url || item.title);
  if (!logos.length) {
    return null;
  }
  const slides = [];
  while (slides.length < 10) {
    slides.push(...logos);
  }
  const loop = [...slides, ...slides];
  const seconds = Math.max(48, Math.round(slides.length * 2.75));
  const close = () => setMake(null);
  const mark = (item) =>
    item.logo_url ? <img src={item.logo_url} alt={quiet ? "" : item.title} /> : <span>{item.title}</span>;

  return (
    <>
      <div
        className={quiet ? "logo-marquee is-quiet is-makes" : "logo-marquee is-makes"}
        style={{ "--logo-scroll": `${seconds}s` }}
        aria-label={quiet ? undefined : "Manufactures"}
        aria-hidden={quiet || undefined}
      >
        <div className="logo-track">
          {loop.map((item, index) =>
            quiet ? (
              <span key={`${item.id}-${index}`} className="logo-hit">
                {mark(item)}
              </span>
            ) : (
              <button
                key={`${item.id}-${index}`}
                type="button"
                className="logo-hit"
                onClick={() => setMake(item)}
              >
                {mark(item)}
              </button>
            )
          )}
        </div>
      </div>
      {quiet ? null : (
      <Modal show={Boolean(make)} onHide={close} centered className="make-modal">
        <Modal.Body>
          <div className="make-modal-split">
            <div className="make-modal-logo-wrap">
              {make?.logo_full_url || make?.logo_url ? (
                <img className="make-modal-logo" src={make.logo_full_url || make.logo_url} alt="" />
              ) : null}
            </div>
            <div className="make-modal-copy">
              <div className="make-modal-head">
                <h2>{make?.title}</h2>
                <button type="button" className="btn btn-site-quiet make-back" onClick={close}>
                  Back
                </button>
              </div>
              <p className="make-history">
                {make?.history?.trim() ? make.history : "No history has been written for this manufacture yet."}
              </p>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      )}
    </>
  );
}

export function eventHeroSlides(event, winners) {
  if (!event) {
    return [];
  }
  const slides = [];
  if (event.image_main_url) {
    slides.push({ src: event.image_main_url, kind: "show" });
  }
  (event.photos || []).forEach((photo) => {
    if (photo.on_hero && photo.image) {
      slides.push({ src: photo.image, kind: "show" });
    }
  });
  (winners || []).forEach((winner) => {
    if (String(winner.event) === String(event.id) && winner.image) {
      slides.push({ src: winner.image, kind: "winner", winner });
    }
  });
  return slides;
}

export function eventSlides(event) {
  if (!event) {
    return [];
  }
  const slides = [];
  if (event.image_main_url) {
    slides.push({ src: event.image_main_url, caption: event.title });
  }
  if (event.image_secondary_url) {
    slides.push({ src: event.image_secondary_url, caption: event.title });
  }
  (event.photos || []).forEach((photo) => {
    if (photo.on_gallery && photo.image) {
      slides.push({ src: photo.image, caption: photo.photographer || event.title });
    }
  });
  (event.lunches || []).forEach((lunch) => {
    if (lunch.logo_url) {
      slides.push({ src: lunch.logo_url, caption: lunch.title });
    }
  });
  return slides;
}

export function PhotoCarousel({ slides, renderCard }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = slides.length;

  useEffect(() => {
    setIndex(0);
  }, [count]);

  useEffect(() => {
    if (count < 2 || paused) {
      return undefined;
    }
    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, 5000);
    return () => clearInterval(timer);
  }, [count, paused]);

  const stillInside = (event) => {
    const next = event.relatedTarget;
    return next instanceof Node && event.currentTarget.contains(next);
  };

  const hold = (event) => {
    if (stillInside(event)) {
      return;
    }
    setPaused(true);
  };

  const release = (event) => {
    if (stillInside(event)) {
      return;
    }
    setPaused(false);
  };

  if (!count) {
    return null;
  }

  return (
    <section
      className="photo-hero"
      aria-label="Event photos"
      onMouseEnter={hold}
      onMouseLeave={release}
      onFocus={hold}
      onBlur={release}
    >
      {slides.map((item, itemIndex) => (
        <div
          key={`${item.src}-${itemIndex}`}
          className={itemIndex === index ? "photo-hero-slide is-on" : "photo-hero-slide"}
          style={{ backgroundImage: `url(${item.src})` }}
        >
          {item.caption ? <p className="photo-hero-caption">{item.caption}</p> : null}
        </div>
      ))}
      {renderCard ? (
        <div className={slides[index]?.kind === "winner" ? "hero-card" : "hero-card hero-card-fit"}>
          {renderCard(slides[index])}
        </div>
      ) : null}
      {count > 1 && (
        <>
          <button
            type="button"
            className="photo-hero-nav photo-hero-prev"
            onClick={() => setIndex((current) => (current - 1 + count) % count)}
            aria-label="Previous photo"
          >
            ‹
          </button>
          <button
            type="button"
            className="photo-hero-nav photo-hero-next"
            onClick={() => setIndex((current) => (current + 1) % count)}
            aria-label="Next photo"
          >
            ›
          </button>
        </>
      )}
    </section>
  );
}

export function MenuLink({ href }) {
  return (
    <a className="admit-menu" href={href} target="_blank" rel="noreferrer">
      <svg className="admit-menu-icon" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M3 2.2h7.1L13 5v8.8H3z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        <path d="M10 2.4V5.1H13" fill="none" stroke="currentColor" strokeWidth="1.3" />
        <path d="M5 7.3h6M5 9.5h6M5 11.7h4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
      View menu
    </a>
  );
}

export function LunchBanner({ lunch, onGetTickets }) {
  if (!lunch) {
    return null;
  }
  const tickets = lunch.ticket_types || [];
  const hours = lunchHours(lunch);
  const ticketsButton = onGetTickets ? (
    <button type="button" className="btn btn-site" onClick={onGetTickets}>
      Get tickets
    </button>
  ) : (
    <Link className="btn btn-site" to="/luncheon">
      Get tickets
    </Link>
  );

  return (
    <aside className="lunch-banner">
      <div className="lunch-logo">
        {lunch.logo_url ? <img src={lunch.logo_url} alt="" /> : <span className="admit-mark">VIP</span>}
      </div>
      <div className="lunch-copy">
        <div className="lunch-main">
          <div className="lunch-lead">
            <p className="site-kicker lunch-kicker">
              <span>Luncheon</span>
              {lunch.hosted ? (
                <>
                  <span className="lunch-split" aria-hidden="true">|</span>
                  <span className="lunch-host">Hosted by {lunch.hosted}</span>
                </>
              ) : null}
            </p>
            <h2>{lunch.title}</h2>
            {hours || lunch.menu_url ? (
              <div className="lunch-details">
                {hours ? (
                  <p className="lunch-detail">
                    <span className="lunch-detail-label">TIME:</span>
                    <span>{hours}</span>
                  </p>
                ) : null}
                {lunch.menu_url ? (
                  <p className="lunch-detail">
                    <span className="lunch-detail-label">MENU:</span>
                    <MenuLink href={lunch.menu_url} />
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          {tickets.length > 0 ? (
            <div className="lunch-prices">
              <h3>Ticket pricing</h3>
              <hr />
              <dl>
                {tickets.map((ticket) => (
                  <div key={ticket.id}>
                    <dt>{ticket.name}</dt>
                    <dd>{money(ticket.price) || "no charge"}</dd>
                  </div>
                ))}
              </dl>
              <div className="lunch-actions">{ticketsButton}</div>
            </div>
          ) : (
            <div className="lunch-actions">{ticketsButton}</div>
          )}
        </div>
      </div>
    </aside>
  );
}
